/**
 * The client **death-overlay state machine** (Player duels, PRD #252 story 21,
 * Duel slice E, ADR-0136).
 *
 * The full Ultima Online death cinematic that the *dying player's own* client
 * runs: a hard cut to **black** with **"You are dead"** centred, a fade into a
 * fully **desaturated (grayscale) ghost view** held for as long as they are dead,
 * then a clean **clear** at resurrection. Other clients see none of this - they
 * simply watch the loser's replicated body become the solid, grayscale death
 * shroud (the server's body swap, ADR-0142); this overlay is purely local to the
 * one who died.
 *
 * This module is the **pure** decision layer only - a clock-free reducer over an
 * explicit phase plus an elapsed-ms counter, advanced by the caller's per-frame
 * delta, exactly the way the duel fight core (`sim/duel.ts`) is advanced by server
 * ticks. It reads no DOM and no Pixi. `main.ts` feeds it the two signals it derives
 * from the local player's replicated health (0 = died, a jump back to full = the
 * same resurrection moment the fight slice already fires) and renders
 * {@link deathOverlayView} to the black veil + "You are dead" line + canvas
 * grayscale on its own side of the split (`src/deathScreen.ts`). Kept pure so the
 * black -> fade -> desaturated-hold -> clear transitions are node-tested with
 * explicit deltas, while the DOM/filter wiring is verified in the two-client
 * browser harness (the definition of done).
 */

/**
 * Ms the hard cut to black (with "You are dead") is held before it begins fading
 * into the desaturated ghost view. Together with {@link DEATH_FADE_MS} the whole
 * black->desaturated transition fits well inside the ~2.5s death beat
 * (`DUEL_DEATH_BEAT_TICKS`), leaving a beat of pure grayscale hold before the
 * resurrection lifts everything. Tunable.
 */
export const DEATH_BLACK_HOLD_MS = 900;

/** Ms the black veil (and its "You are dead" line) takes to fade out, revealing the grayscale view. */
export const DEATH_FADE_MS = 700;

/**
 * How far the death cinematic has progressed:
 *  - `alive`   - no overlay; the ordinary living view.
 *  - `black`   - the hard cut to black, "You are dead" centred (held for {@link DEATH_BLACK_HOLD_MS}).
 *  - `fading`  - the black veil + message fading out over {@link DEATH_FADE_MS}, the grayscale revealing beneath.
 *  - `ghost`   - the veil gone, the game view held fully desaturated for as long as the player is dead.
 */
export type DeathOverlayPhase = 'alive' | 'black' | 'fading' | 'ghost';

/**
 * The overlay's state: its {@link DeathOverlayPhase} plus the ms elapsed in the
 * current *timed* phase (`black`/`fading`). `alive` and `ghost` are steady states
 * that ignore the counter and only leave on an explicit death/resurrect signal.
 */
export interface DeathOverlayState {
  phase: DeathOverlayPhase;
  /** Ms elapsed in the current timed phase; unused (0) in the steady `alive`/`ghost` states. */
  phaseMs: number;
}

/** The render-content of the overlay this frame, derived purely from {@link DeathOverlayState}. */
export interface DeathOverlayView {
  /** Alpha `[0, 1]` of the full-screen black veil (1 = the hard cut to black, 0 = gone). */
  blackAlpha: number;
  /** Alpha `[0, 1]` of the centred "You are dead" line (rides atop the black veil). */
  messageAlpha: number;
  /** Whether the game view is rendered fully desaturated (grayscale) this frame. */
  grayscale: boolean;
}

/** The initial (living) overlay state. */
export function initialDeathOverlay(): DeathOverlayState {
  return { phase: 'alive', phaseMs: 0 };
}

/**
 * Begin the death cinematic (the local player's health reached 0): a hard cut to
 * black. **Idempotent** off `alive` - a second death signal while already dying or
 * dead (repeated 0-health snapshots stream at 10Hz, ADR-0163) leaves the running sequence
 * untouched, so the cinematic never restarts mid-fade.
 */
export function applyDeath(state: DeathOverlayState): DeathOverlayState {
  if (state.phase !== 'alive') return state;
  return { phase: 'black', phaseMs: 0 };
}

/**
 * Clear the death cinematic (the resurrection moment the fight slice fires - the
 * local player's health jumped back to full): back to the living view. A no-op
 * when already `alive`, so a stray resurrect signal is harmless.
 */
export function applyResurrect(state: DeathOverlayState): DeathOverlayState {
  if (state.phase === 'alive') return state;
  return { phase: 'alive', phaseMs: 0 };
}

/**
 * Advance the cinematic by `dtMs` (one frame's delta). The steady `alive`/`ghost`
 * states are unchanged (they only leave on a death/resurrect signal); `black`
 * advances to `fading` after {@link DEATH_BLACK_HOLD_MS} and `fading` to `ghost`
 * after {@link DEATH_FADE_MS}, carrying any overflow forward so a large delta
 * cannot stall the sequence. A negative or absent delta is clamped to 0.
 */
export function advanceDeathOverlay(state: DeathOverlayState, dtMs: number): DeathOverlayState {
  if (state.phase === 'alive' || state.phase === 'ghost') return state;
  let phase = state.phase;
  let phaseMs = state.phaseMs + Math.max(0, dtMs);
  // Cascade through as many phase boundaries as this delta crosses, carrying the
  // overflow forward, so even a single large frame delta settles fully rather than
  // stalling one transition per call.
  if (phase === 'black' && phaseMs >= DEATH_BLACK_HOLD_MS) {
    phase = 'fading';
    phaseMs -= DEATH_BLACK_HOLD_MS;
  }
  if (phase === 'fading' && phaseMs >= DEATH_FADE_MS) {
    return { phase: 'ghost', phaseMs: 0 };
  }
  return { phase, phaseMs };
}

/**
 * The pure render-content for a state: the black-veil and message alphas and
 * whether the view is desaturated. Grayscale is on from the instant of death (it
 * sits hidden behind the black veil through `black`/`fading`, so when the veil
 * lifts the world is *already* grey - no pop) and stays on through `ghost` until
 * resurrection returns the state to `alive`.
 */
export function deathOverlayView(state: DeathOverlayState): DeathOverlayView {
  switch (state.phase) {
    case 'alive':
      return { blackAlpha: 0, messageAlpha: 0, grayscale: false };
    case 'black':
      return { blackAlpha: 1, messageAlpha: 1, grayscale: true };
    case 'fading': {
      const remaining = 1 - Math.min(1, state.phaseMs / DEATH_FADE_MS);
      return { blackAlpha: remaining, messageAlpha: remaining, grayscale: true };
    }
    case 'ghost':
      return { blackAlpha: 0, messageAlpha: 0, grayscale: true };
  }
}
